import { useMemo } from 'react';
import { Heart } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useOutboxPublish } from '@/hooks/useOutboxPublish';
import { useToast } from '@/hooks/useToast';
import { articleCoordinate } from '@/lib/articleUtils';
import { cn } from '@/lib/utils';

interface ReactionButtonProps {
  article: NostrEvent;
  reactions: NostrEvent[];
  coordinate: string;
}

export function ReactionButton({ article, reactions, coordinate }: ReactionButtonProps) {
  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending } = useOutboxPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const hasReacted = useMemo(
    () =>
      !!user &&
      reactions.some((r) => r.pubkey === user.pubkey && r.content !== '-'),
    [reactions, user],
  );

  const likeCount = useMemo(
    () => reactions.filter((r) => r.content !== '-').length,
    [reactions],
  );

  const handleReact = async () => {
    if (!user) {
      toast({ title: 'Log in to react', variant: 'destructive' });
      return;
    }
    if (hasReacted) return;

    try {
      await publish({
        kind: 7,
        content: '+',
        tags: [
          ['a', coordinate, ''],
          ['e', article.id],
          ['k', String(article.kind)],
          ['p', article.pubkey],
        ],
      });
      toast({ title: 'Reaction sent ❤️' });
      queryClient.invalidateQueries({
        queryKey: ['article-interactions', articleCoordinate(article)],
      });
    } catch {
      toast({ title: 'Failed to send reaction', variant: 'destructive' });
    }
  };

  return (
    <Button
      variant={hasReacted ? 'default' : 'outline'}
      size="sm"
      onClick={handleReact}
      disabled={isPending || hasReacted || !user}
      className="gap-2 rounded-full"
    >
      <Heart className={cn('h-4 w-4', hasReacted && 'fill-current')} />
      <span>{hasReacted ? 'Liked' : 'Like'}</span>
      {likeCount > 0 && <span className="opacity-80">· {likeCount}</span>}
    </Button>
  );
}
